import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { ChangeEvent, useMemo, useState } from 'react';
import { Sliders } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import { CreditCoinIcon } from 'src/components/Icons';
import InputSearch from 'src/components/InputSearch';
import CustomTable from 'src/components/Table';
import { getActivityChip } from 'src/features/Activities/common';
import { TypeActivitiesTab } from 'src/features/Activities/config';
import PopupFilter from 'src/features/Activities/PopupFilter';
import { useQueryParsedParams } from 'src/hooks/useQueryParam';
import { useQueryCreditUsageLog, useQueryUsageInformation } from 'src/services/queries';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST, LIMIT_GET_LIST_BILLS_OF_LADING } from 'src/utils/constants/config';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { TypeCreditUsageLog } from 'src/utils/typings/dto/payment';

const CreditUsageLog = () => {
  const [visibleFilter, setVisibleFilter] = useState(false as boolean);
  const { data: userUsage } = useQueryUsageInformation();
  const { tab } = useQueryParsedParams();

  const payloadRequest = useMemo(() => {
    if (tab !== TypeActivitiesTab.TEAM_ACTIVITIES) return {};
    return {
      team_id: userUsage.data?.team_id ?? 0,
    };
  }, [tab, userUsage]);

  const [params, setParams] = useState({
    limit: LIMIT_GET_LIST,
    offset: 0,
    search: '',
  });
  const { data, isFetching } = useQueryCreditUsageLog({ ...params, ...payloadRequest });

  const handleSearch = debounce(
    (e: ChangeEvent<HTMLInputElement>) => setParams({ ...params, ...{ search: e.target.value } }),
    800,
  );

  return (
    <WhiteBoxContent>
      <PopupFilter
        params={params}
        onFilter={(payload) => setParams({ ...params, ...payload })}
        open={visibleFilter}
        handleClose={() => setVisibleFilter(false)}
      />
      <Stack spacing={3}>
        <Typography variant="h6">Credit Usage Log </Typography>
        <Stack direction={'row'} spacing={2}>
          <InputSearch onChange={handleSearch} sx={{ flex: 1 }} size="small" placeholder="Search Identity" />
          <ButtonWithLoading
            onClick={() => setVisibleFilter(true)}
            variant="outlined"
            startIcon={<Sliders size={18} />}
          >
            Filter
          </ButtonWithLoading>
        </Stack>

        <CustomTable
          maxRow={LIMIT_GET_LIST_BILLS_OF_LADING}
          totalRecords={data.total_records}
          onChangePage={(e) => setParams({ ...params, ...{ offset: e } })}
          onChangePageSize={(e) => setParams({ ...params, ...{ offset: 0, limit: e } })}
          page={params.offset}
          pageSize={params.limit}
          loading={isFetching}
          data={data.data}
          configColumn={
            [
              {
                id: 'identity',
                label: 'Identity',
                cell: (data: TypeCreditUsageLog) => (
                  <RouterLinkNoUnderline to={data.web_url_usage || ''}>
                    <Typography color={'secondary.main'} variant="body2">
                      {renderTextOrDefault(data.identity)}
                    </Typography>
                  </RouterLinkNoUnderline>
                ),
                width: 250,
                disableSort: true,
              },
              {
                id: 'feature_category',
                label: 'Feature Category',
                cell: (data: TypeCreditUsageLog) => (
                  <Typography color={'text.secondary'} variant="body2">
                    {data.feature_category}
                  </Typography>
                ),
                width: 250,
                disableSort: true,
              },
              {
                id: 'spent_date',
                label: 'Spent Date',
                width: 180,
                cell: (data: TypeCreditUsageLog) => (
                  <Typography noWrap color={'text.secondary'} variant="body2">
                    {dayjs(data.spent_date, FORMAT_DATE_TIME.UTC).format(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A)}
                  </Typography>
                ),
                disableSort: true,
              },
              {
                id: 'exp_date',
                label: 'Exp. Date',
                width: 180,
                cell: (data: TypeCreditUsageLog) => (
                  <Typography noWrap color={'text.secondary'} variant="body2">
                    {dayjs(data.exp_date, FORMAT_DATE_TIME.UTC).format(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A)}
                  </Typography>
                ),
                disableSort: true,
              },
              {
                id: 'activities',
                label: 'Activity',
                width: 120,
                cell: (data: TypeCreditUsageLog) => {
                  return getActivityChip(data.activity);
                },
                disableSort: true,
              },
              {
                id: 'spent',
                label: 'Spent',
                cell: (data: TypeCreditUsageLog) => (
                  <Typography alignItems={'center'} display={'flex'} gap={0.5} color={'text.secondary'} variant="body2">
                    {data.spent} <CreditCoinIcon fontSize="small" />
                  </Typography>
                ),
                disableSort: true,
              },
              // {
              //   id: 'url',
              //   label: 'URL',
              //   cell: (data: TypeCreditUsageLog) => (
              //     <RouterLinkNoUnderline to={getStringOrDefault(data.web_url_usage)}>
              //       {data.web_url_usage ? (
              //         <IconFeather icon={<ArrowUpRight />} muiProps={{ color: 'secondary' }} />
              //       ) : (
              //         EmptyText
              //       )}
              //     </RouterLinkNoUnderline>
              //   ),
              //   disableSort: true,
              // },
            ] as Array<ConfigCell<TypeCreditUsageLog>>
          }
          dataKey="id"
        />
      </Stack>
    </WhiteBoxContent>
  );
};

export default CreditUsageLog;
